import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';
import { useToast } from '../components/Toast';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&h=600&fit=crop';

const CategoryPage = ({ navigate, category }) => {
  const toast = useToast();
  const [packages, setPackages] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    month: '',
    year: '',
    min_price: '',
    max_price: '',
    tags: '',
    days: ''
  });
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initialize category filter from prop
  useEffect(() => {
    if (category && category !== filters.category) {
      setFilters(prev => ({ ...prev, category }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    loadTags();
    loadCategories();
    loadAllPackages(); // Load all packages for tab counts
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    setError('');
    try {
      const filterParams = {};
      if (filters.category) filterParams.category__slug = filters.category;
      if (filters.month) filterParams.month = filters.month;
      if (filters.year) filterParams.year = filters.year;
      if (filters.min_price) filterParams.min_price = filters.min_price;
      if (filters.max_price) filterParams.max_price = filters.max_price;
      if (filters.tags) filterParams.tags = filters.tags;
      if (filters.days) filterParams.duration_days = filters.days;

      const data = await api.getPackages(filterParams);
      setPackages(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      console.error('Error loading packages:', err);
      setError(err.message || 'Unable to load packages. Please try again.');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllPackages = async () => {
    try {
      const data = await api.getPackages({});
      const packages = Array.isArray(data) ? data : (data.results || []);
      setAllPackages(packages);
    } catch (error) {
      console.error('Error loading all packages:', error);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.getTags();
      const tagsArray = Array.isArray(data) ? data : (data.results || []);
      setTags(tagsArray);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      const categoriesArray = Array.isArray(data) ? data : (data.results || []);
      setCategories(categoriesArray);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar navigate={navigate} />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24 md:pt-28">
        <h1 className="text-2xl sm:text-4xl font-bold mb-6 capitalize">
          {filters.category ? (categories.find(c => c.slug === filters.category)?.name || 'All Packages') : 'All Packages'}
        </h1>
        
        {/* Category Tabs */}
        <div className="mb-8 bg-white rounded-xl shadow-lg p-3 overflow-hidden">
          <div className="flex gap-3 overflow-x-auto touch-scroll pb-1">
            <button
              onClick={() => handleFilterChange('category', '')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                filters.category === '' 
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg' 
                  : 'bg-sand text-ink/80 hover:bg-accent/20'
              }`}>
              🌟 All Packages ({allPackages.length})
            </button>
            {categories.filter(cat => cat.category_type === 'package').map(cat => {
              const categoryPackages = allPackages.filter(pkg => pkg.category_name === cat.name);
              return (
                <button
                  key={cat.id}
                  onClick={() => handleFilterChange('category', cat.slug)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    filters.category === cat.slug 
                      ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg' 
                      : 'bg-sand text-ink/80 hover:bg-accent/20'
                  }`}>
                  {cat.name} ({categoryPackages.length})
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Filter Packages</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2">
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Month</label>
              <select value={filters.month} onChange={(e) => handleFilterChange('month', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2">
                <option value="">All Months</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Year</label>
              <select value={filters.year} onChange={(e) => handleFilterChange('year', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2">
                <option value="">All Years</option>
                {[2026, 2027, 2028].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Min Price</label>
              <input type="number" value={filters.min_price} onChange={(e) => handleFilterChange('min_price', e.target.value)}
                     className="w-full border rounded-lg px-3 py-2" placeholder="Min" />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Max Price</label>
              <input type="number" value={filters.max_price} onChange={(e) => handleFilterChange('max_price', e.target.value)}
                     className="w-full border rounded-lg px-3 py-2" placeholder="Max" />
            </div>
          </div>

          {/* Duration Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Duration (Days)</label>
            <div className="flex flex-wrap gap-2">
              {['', '7', '10', '14', '21', '28'].map(d => (
                <button
                  key={d}
                  onClick={() => handleFilterChange('days', d)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    filters.days === d
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-ink/80 border-sand hover:border-primary'
                  }`}>
                  {d === '' ? 'All Days' : `${d} Days`}
                </button>
              ))}
            </div>
          </div>

          {/* Tags Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFilterChange('tags', '')}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  filters.tags === ''
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                }`}>
                All Tags
              </button>
              {tags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => handleFilterChange('tags', filters.tags === tag.slug ? '' : tag.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    filters.tags === tag.slug
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-ink/80 border-sand hover:border-primary'
                  }`}>
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Packages Grid */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
            <span className="text-sm">{error}</span>
            <button onClick={loadPackages} className="font-semibold underline text-sm">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-sand rounded-2xl animate-pulse" />)}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <div key={pkg.id} onClick={() => navigate('package', { package: pkg })}
                 className="lux-card cursor-pointer overflow-hidden">
              <img
                src={pkg.featured_image || PLACEHOLDER_IMG}
                alt={pkg.name}
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
              />
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                <p className="text-ink/65 mb-4 text-sm line-clamp-2">{pkg.short_description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {pkg.tags?.map(tag => (
                    <span key={tag.id} className="bg-cream text-primary px-2 py-1 rounded text-xs font-medium">
                      {tag.name}
                    </span>
                  ))}
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-ink/50">From</p>
                    <p className="text-2xl font-bold text-primary">${pkg.min_price}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-ink/50">{pkg.duration_days}D/{pkg.duration_nights}N</p>
                      <p className="text-sm font-medium">
                        {pkg.travel_date ? new Date(pkg.travel_date).toLocaleDateString() : ''}
                      </p>
                    </div>
                    {/* Share button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/?package=${pkg.slug}`;
                        if (navigator.share) {
                          navigator.share({ title: pkg.name, text: `Check out this Umrah package: ${pkg.name}`, url });
                        } else {
                          navigator.clipboard.writeText(url).then(() => {
                            toast.success('Link copied!');
                          }).catch(() => {
                            const ta = document.createElement('textarea');
                            ta.value = url;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success('Link copied!');
                          });
                        }
                      }}
                      title="Share this package"
                      className="w-9 h-9 rounded-full border border-sand hover:border-accent bg-white flex items-center justify-center text-ink/50 hover:text-primary transition-all shrink-0 shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {!loading && packages.length === 0 && !error && (
          <div className="text-center py-16 bg-white rounded-2xl border border-sand">
            <p className="text-xl text-ink/50 mb-4">No packages found matching your criteria.</p>
            <button onClick={() => setFilters({ category: '', month: '', year: '', min_price: '', max_price: '', tags: '', days: '' })}
              className="text-primary font-semibold hover:underline">Clear filters</button>
          </div>
        )}
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default CategoryPage;
